import { z } from 'zod';

import type {
  Order,
  OrderLine,
  PatchAdminV1StoresStoreIdOrdersIdBody,
  PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody,
} from '@nucleus-commerce/api-client/admin';

/** Email, phone and the shopper's note; totals never change after placement. */
export const contactSchema = z.object({
  email: z
    .string()
    .trim()
    .refine((v) => v === '' || z.email().safeParse(v).success, {
      error: 'orders:editContact.validation.email',
    }),
  phone: z.string().trim().max(40, { error: 'orders:editContact.validation.phoneLength' }),
  note: z.string().trim().max(2000, { error: 'orders:editContact.validation.noteLength' }),
});

export type ContactForm = z.infer<typeof contactSchema>;

export function fromOrderContact(order: Pick<Order, 'email' | 'phone' | 'note'>): ContactForm {
  return { email: order.email ?? '', phone: order.phone ?? '', note: order.note ?? '' };
}

/**
 * Only fields that changed go out. The email cannot be cleared through the API (it is a
 * required format when sent), so a blank email on a walk-in order simply stays unset.
 */
export function toContactPatch(
  values: ContactForm,
  order: Pick<Order, 'email' | 'phone' | 'note'>,
): PatchAdminV1StoresStoreIdOrdersIdBody {
  const body: PatchAdminV1StoresStoreIdOrdersIdBody = {};
  if (values.email !== '' && values.email !== order.email) body.email = values.email;
  const phone = values.phone === '' ? null : values.phone;
  if (phone !== order.phone) body.phone = phone;
  const note = values.note === '' ? null : values.note;
  if (note !== order.note) body.note = note;
  return body;
}

/**
 * Lines whose answers staff may correct: a catalogue line with something left unshipped. Custom
 * (draft) lines and shipped or cancelled units answer 409.
 */
export function canEditLine(
  order: Pick<Order, 'status'>,
  line: Pick<OrderLine, 'variantId' | 'quantity' | 'fulfilledQuantity' | 'cancelledQuantity'>,
): boolean {
  return (
    order.status === 'open' &&
    line.variantId !== null &&
    line.fulfilledQuantity === 0 &&
    line.cancelledQuantity < line.quantity
  );
}

/** The design is waiting on the shopper (or on staff to record the answer). */
export function canRecordApproval(order: Pick<Order, 'status' | 'approvalStatus'>): boolean {
  return (
    order.status === 'open' && order.approvalStatus !== null && order.approvalStatus !== 'approved'
  );
}

export const recordApprovalSchema = z
  .object({
    outcome: z.enum(['approved', 'changes_requested']),
    note: z.string().trim().max(2000, { error: 'orders:recordApproval.validation.noteLength' }),
  })
  .refine((v) => v.outcome === 'approved' || v.note !== '', {
    path: ['note'],
    error: 'orders:recordApproval.validation.noteRequired',
  });

export type RecordApprovalForm = z.infer<typeof recordApprovalSchema>;

export function toRecordApprovalBody(
  values: RecordApprovalForm,
): PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody {
  return { outcome: values.outcome, note: values.note === '' ? null : values.note };
}
